/* import __COLOCATED_TEMPLATE__ from './custom-action-finished.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type CustomActionFinished from 'embercom/objects/inbox/renderable/custom-action-finished';
import LinkWithText from 'embercom/helpers/link-with-text';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Router from '@ember/routing/router-service';
import { truncateEventText, isEventTextTruncated } from 'embercom/lib/inbox2/event-text-truncation';

interface CustomActionFinishedPart extends RenderablePart {
  renderableData: CustomActionFinished;
}

interface Args {
  part: CustomActionFinishedPart;
}

interface Signature {
  Args: Args;
}

const HIGH_LATENCY_THRESHOLD_MS = 1000; // 1 second
const LARGE_PAYLOAD_THRESHOLD_BYTES = 50 * 1024; // 50 KB

export default class CustomActionFinishedPartComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare router: Router;

  @tracked openErrorSection = '';

  // @ts-ignore helper doesn't seem to be typed
  linkWithTextHelper = LinkWithText.create(this);

  get customActionLogsUrl() {
    if (this.customActionId) {
      let customActionUrl: string;

      if (this.session.workspace.canUseFeature('data-connectors-health-page')) {
        let healthPath = this.router.urlFor(
          'apps.app.settings.app-settings.custom-actions.custom-action-health',
          this.session.workspace.id,
          this.customActionId,
        );
        healthPath += '?tab=logs';
        if (this.args.part.renderableData.actionExecutionResultId) {
          healthPath += `&logId=${this.args.part.renderableData.actionExecutionResultId}`;
        }
        customActionUrl = window.location.origin + healthPath;
      } else {
        let queryParams: { selectedTab: string; actionExecutionResultId?: number } = {
          selectedTab: 'logs',
        };

        if (this.args.part.renderableData.actionExecutionResultId) {
          queryParams.actionExecutionResultId =
            this.args.part.renderableData.actionExecutionResultId;
        }

        customActionUrl =
          window.location.origin +
          this.router.urlFor(
            'apps.app.settings.app-settings.custom-actions.custom-action',
            this.session.workspace.id,
            this.customActionId,
            { queryParams },
          );
      }

      return this.linkWithTextHelper.compute_with_href(
        customActionUrl,
        this.intl.t('inbox.custom-action-logs.text'),
        false,
        true,
      );
    }
    return;
  }

  get customActionName() {
    if (this.customActionId) {
      return this.linkWithTextHelper.compute([
        'apps.app.settings.app-settings.custom-actions.custom-action',
        this.customActionId,
        this.args.part.renderableData.customActionName,
      ]);
    }
    return this.args.part.renderableData.customActionName;
  }

  get truncatedCustomActionName() {
    if (this.customActionId) {
      return this.linkWithTextHelper.compute([
        'apps.app.settings.app-settings.custom-actions.custom-action',
        this.customActionId,
        truncateEventText(this.args.part.renderableData.customActionName),
      ]);
    }
    return truncateEventText(this.args.part.renderableData.customActionName);
  }

  get rawCustomActionName() {
    return this.args.part.renderableData.customActionName;
  }

  get isCustomActionNameTruncated() {
    return isEventTextTruncated(this.args.part.renderableData.customActionName);
  }

  get customActionId() {
    return this.args.part.renderableData.customActionId;
  }

  get customActionResult() {
    return this.args.part.renderableData.customActionResult;
  }

  get usedSampleResponseData(): boolean {
    return this.args.part.renderableData.usedSampleResponseData;
  }

  get isHighLatency(): boolean {
    return (this.args.part.renderableData.latencyMs ?? 0) > HIGH_LATENCY_THRESHOLD_MS;
  }

  get latencyInSeconds(): string | null {
    if (this.args.part.renderableData.latencyMs) {
      return (this.args.part.renderableData.latencyMs / 1000).toFixed(1);
    }
    return null;
  }

  get isLargePayload(): boolean {
    return (
      (this.args.part.renderableData.responsePayloadSizeBytes ?? 0) > LARGE_PAYLOAD_THRESHOLD_BYTES
    );
  }

  get payloadSizeInKB(): string | null {
    if (this.args.part.renderableData.responsePayloadSizeBytes) {
      return (this.args.part.renderableData.responsePayloadSizeBytes / 1024).toFixed(1);
    }
    return null;
  }

  get translatedResult() {
    if (this.customActionResult === 'failed') {
      // Failure message doesn't depend on latency/payload size
      return 'inbox.custom-action-failed';
    }

    return this.isHighLatency || this.isLargePayload
      ? 'inbox.custom-action-succeeded-high-latency-or-payload'
      : 'inbox.custom-action-succeeded'; // Only name needed
  }

  get isFailed() {
    return this.customActionResult === 'failed';
  }

  get translationKey() {
    return this.renderableData.customBotSummary
      ? this.translatedResultWithCustomBotTitle
      : this.translatedResult;
  }

  get httpStatus() {
    return this.args.part.renderableData.httpStatus;
  }

  get statusSuffix(): string {
    return this.httpStatus != null ? ` (${this.httpStatus})` : '';
  }

  get errorMessage() {
    return this.args.part.renderableData.errorMessage;
  }

  @action
  toggleErrorSection() {
    this.openErrorSection = this.openErrorSection === 'open_error' ? '' : 'open_error';
  }

  get renderableData() {
    return this.args.part.renderableData;
  }

  get translatedResultWithCustomBotTitle() {
    if (this.customActionResult === 'failed') {
      // Failure message doesn't depend on latency/payload size
      return 'inbox.custom-action-failed-with-custom-bot-title';
    }

    return this.isHighLatency || this.isLargePayload
      ? 'inbox.custom-action-succeeded-with-custom-bot-title-high-latency-or-payload'
      : 'inbox.custom-action-succeeded-with-custom-bot-title'; // Only name needed
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::CustomActionFinished': typeof CustomActionFinishedPartComponent;
    'inbox2/conversation-stream/event-parts/custom-action-finished': typeof CustomActionFinishedPartComponent;
  }
}
